import { h, Component } from 'preact';
import Store from 'store/store';

import ScrollBox from 'components/shared/scrollBox/scrollBox';
import Button from 'components/bolt/button/button';
import { smoothScrollTo } from 'utils/smoothScroll';
import { joinClasses, accessNested } from 'utils/utils';
import { expandFilterSelection } from 'utils/bolt/showtimes';
import formatDateTime from 'utils/formatDateTime';
import Reset from 'components/bolt/icons/reset';
import Chevron from 'components/bolt/icons/chevron';

import s from 'components/bolt/showtimes/filterSelection.sss';

export default class FilterSelection extends Component {
	constructor(props) {
		super(props);
		this.dateClickHandlers = {};
		this.clearAll = this.clearAll.bind(this);
		this.datesKeyDown = this.datesKeyDown.bind(this);
		this.scrollRight = this.scrollRight.bind(this);
		this.scrollLeft = this.scrollLeft.bind(this);
		this.today = formatDateTime(new Date(), 'yyyy-MM-dd');
	}

	componentDidUpdate(prevProps) {
		if (prevProps.filters !== this.props.filters) {
			this.setDatesInScroller();
		}
	}

	setDatesInScroller() {
		const { filters } = this.props;
		let dates;
		let selected;
		if (!filters.loading && filters.available && filters.available.date) {
			dates = filters.available.date;
			// if (!dates.includes(this.today)) {
			// 	dates = dates.concat(this.today).sort();
			// }
			if (!dates.length) {
				dates = [this.today];
			}
			selected = accessNested(filters, 'selected.date') || [];
		} else {
			dates = [this.today];
			selected = dates;
		}
		dates = dates.filter(d => d >= this.today).sort();

		this.setState({
			datesInScroller: dates,
			selectedDate: selected
		});
	}

	focus() {
		if (this.$dates && this.$dates.base) {
			this.$dates.base.focus();
		}
	}

	dateClicked(date) {
		Store.emit('FILTER_SET', 'date', date);
	}

	getDateClickHandler(date) {
		if (!this.dateClickHandlers[date]) {
			this.dateClickHandlers[date] = this.dateClicked.bind(this, date);
		}
		return this.dateClickHandlers[date];
	}

	datesKeyDown(e) {
		let filters = this.props.filters;
		if (filters.loading || !filters.available || !filters.available.date) {
			return;
		}
		let offset = 0;
		if (e.key === 'ArrowRight') {
			offset = 1;
		}
		if (e.key === 'ArrowLeft') {
			offset = -1;
		}
		if (offset) {
			e.preventDefault();
			let allDates = filters.available.date;
			let firstSelection = accessNested(filters, 'selected.date.0');
			let index = allDates.indexOf(firstSelection);
			let newIndex = index + offset;
			if (index !== -1 && newIndex >= 0 && newIndex < allDates.length) {
				Store.emit('FILTER_SET', 'date', allDates[newIndex]);
				// let target = e.target && e.target.parentElement.querySelector('.' + s.date + ':nth-child(' + (newIndex + 1) + ')');
				// if (target) {
				// 	target.focus();
				// }
			}
		}
	}

	clearAll() {
		Store.emit('FILTERS_RESET');
	}

	isDefault() {
		let filters = this.props.filters;
		let selected = filters.selected;
		if (!selected) {
			return true;
		}
		let firstDate = filters.available && filters.available.date && filters.available.date[0];
		return Object.keys(selected).every(filter => {
			let sel = selected[filter];
			if (!sel || !sel.length) {
				return true;
			}
			return filter === 'date' && sel.length === 1 && (sel[0] === firstDate || sel[0] === this.today);
		});
	}

	getDisplayedFilters(available) {
		let allKeys = Object.keys(available);
		allKeys.sort((a, b) => {
			if (a === 'movie') return -1;
			if (b === 'movie') return 1;
			return 0;
		});

		// const showAudioDetailsAsFormat = Store.get().appData?.pages?.boltShowtimes?.data?.showAudioDetailsAsFormat;
		return allKeys.filter(e => e !== 'date' && e !== 'exhibitor' && available[e]?.length).map(f => {
			if (f === 'audioDetails') {
				return { filter: f, mergeWith: 'format' };
			}
			return { filter: f };
		});
	}

	getFilterName(filter, option) {
		const filterCopy = this.props.appData.copy?.baseCopy?.filters?.[filter] || {};
		const options = filterCopy.useLangOptions ? this.props.appData.copy.baseCopy.languages : filterCopy;

		if (filter === 'movie') {
			const currentRegion = Store.get().client?.geo?.countryCode;
			const movieData = Store.get().appData?.pages?.boltShowtimes?.data?.movieData;
			const equivalentOption = this.props.allShowtimesMovies?.[option];
			const localeMetaTitle = Store.get().appData?.meta?.title?.locale;
			const currentRegionTitle =
				// 1. Thundr movieData Region override title
				movieData?.[currentRegion]?.[option]?.title
				// 2. Thundr movieData Top Level override title
				||	movieData?.[option]?.title
				// 3. Title coming from API for this region
				||	equivalentOption?.regions.find(option => option.country === currentRegion)?.title
				// 4. Title coming from API for INTL region
				|| equivalentOption?.regions.find(option => option.country === 'INTL')?.title
				// 5. Last resort - show the meta locale title for all
				|| localeMetaTitle
				// 6. Very last resort - the movie id itself
				|| option;
			return currentRegionTitle;
		}
		return options[option] || option;
	}

	renderDates() {
		let dateTimeLocale = accessNested(this.props, 'appData.copy.baseCopy.$DATETIME_LOCALE');
		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const dates = this.state.datesInScroller || [];
		const selected = this.state.selectedDate || [];

		return dates.map(dateStr => {
			let parts = dateStr.split('-');
			let date = new Date(+parts[0], parts[1] - 1, +parts[2]);
			let active = selected.includes(dateStr);

			return (
				<Button
					class={joinClasses({ s, date: true, active })}
					trigger={this.getDateClickHandler(dateStr)}
					tabIndex="-1"
					ariaLabel={accessibilityCopy.$DATE_SELECTION_ARIA.replace('<option>', dateStr)}
				>
					<span class={joinClasses(s.month, fontStyles.filterMenuMonth)}>{formatDateTime(date, 'MMM', dateTimeLocale)}</span>
					<span class={joinClasses(s.dom, active && s.active, fontStyles.filterMenuDate)}>{formatDateTime(date, 'dd', dateTimeLocale)}</span>
					<span class={joinClasses(s.day, active && s.active, fontStyles.filterMenuDayActive)}>{formatDateTime(date, 'ddd', dateTimeLocale)}</span>
				</Button>
			);
		});
	}

	renderFilters() {
		let filters = this.props.filters;
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});
		const noFiltersCopy = accessNested(this.props, 'appData.copy.baseCopy.$FILTERS_NONE_AVAILABLE') || 'No filters available';
		const isRTL = fontStyles.textDirection === 'rtl';
		const hideFilterOptionTime = Store.get().appData?.pages?.boltShowtimes?.data?.hideFilterOptionTime;

		if (filters.loading || !filters.available) {
			// Loader ?
			return;
		}
		let appData = this.props.appData;
		// TODO: sort
		let filtersList = this.getDisplayedFilters(filters.available)?.map(f => typeof f === 'string' ? { filter: f } : f)?.filter(e => e);
		if (!filtersList?.length) {
			return (
				<div class={s.empty}>{noFiltersCopy}</div>
			);
		}

		let getSelected = (() => {
			let cache = {};
			return (filter) => cache[filter] || (cache[filter] = expandFilterSelection(filters.selected?.[filter] || [], filters.available?.[filter]));
		})();

		const desktopSize = this.props.client.isDesktopSize;
		return [
			<Button type="text" class={joinClasses(s.reset, this.isDefault() && s.hidden, isRTL && s.rtlReset)} trigger={this.clearAll} key="clear" ariaLabel={accessibilityCopy.$CLEAR_FILTERS_ARIA}>
				{!!desktopSize && <div class={joinClasses(s.buttonCopy, fontStyles.filterMenuClearAll, isRTL && s.rtlButtonCopy)}>{accessNested(appData, 'copy.baseCopy.$CLEAR_ALL') || 'Clear All'}</div>}
				<Reset class={s.resetButton} ariaLabel={accessibilityCopy.$CLEAR_FILTERS_ARIA} />
			</Button>
		].concat(filtersList.map(({ filter, mergeWith }) => {
			if (mergeWith && filtersList.some(f => f.filter === mergeWith)) {
				return;
			}
			let options = filters.available[filter];

			let merges = filtersList.filter(f => f?.mergeWith === filter);
			if (merges.length) {
				options = [filter, ...merges.map(m => m.filter)].flatMap(f => filters.available[f]?.map(opt => ({ value: opt, filter: f })));
			}

			let optionAriaLabel = '';
			if (filter === 'time') {
				if (hideFilterOptionTime) return;
				optionAriaLabel = accessibilityCopy.$TIME_SELECTION_ARIA;

				const order = ['morning', 'afternoon', 'evening', 'late'];
				options = order.filter(tod => options?.includes(tod));
			}

			if (!options.length || options.length <= 1) {
				return;
			}

			let displayedOptions = Object.values(options.reduce((all, option) => {
				if (typeof option === 'string') {
					option = { value: option };
				}
				let currentFilter = option.filter || filter;
				let name = this.getFilterName(currentFilter, option.value);

				let active = getSelected(currentFilter).includes(option.value);
				if (!all[name]) {
					all[name] = { name, active, source: [], filter: currentFilter };
				}
				all[name].source.push(option.value);
				all[name].active = all[name].active || active;
				return all;
			}, {}));

			if (filter === 'format') {
				displayedOptions = displayedOptions.sort((a, b) => a?.name?.localeCompare(b?.name) || 0);
				optionAriaLabel = accessibilityCopy.$FORMAT_SELECTION_ARIA;
			}

			const filterCopy = appData.copy?.baseCopy?.filters?.[filter] || {};
			const filterName = filterCopy?._name || filter;
			return (
				<div class={s.filter} key={filter}>
					<div class={joinClasses(s.name, fontStyles.filterMenuFilterTitle)}>{filterName}</div>
					<div class={s.options}>
						{displayedOptions.map(opt => (
							<Button
								type="tag"
								class={joinClasses(s.option, opt.active && s.active, fontStyles.filterMenuFilter)}
								active={opt.active}
								trigger={() => Store.emit('FILTER_TOGGLE', opt.filter || filter, opt.source)}
								ariaLabel={optionAriaLabel.replace('<option>', opt.name)}
							>
								{opt.name}
							</Button>
						))}
					</div>
				</div>
			);
		}));
	}

	scrollRight() {
		const { $dates } = this;
		const { $scroller } = $dates || {};

		if ($scroller) {
			const scrollTo = $scroller.scrollLeft + 80;
			smoothScrollTo($scroller, scrollTo, 300, { direction: 'hor' });
		}
	}

	scrollLeft() {
		const { $dates } = this;
		const { $scroller } = $dates || {};

		if ($scroller) {
			const scrollTo = $scroller.scrollLeft - 80;
			smoothScrollTo($scroller, scrollTo, 300, { direction: 'hor' });
		}
	}

	render(props) {
		let filters = props.filters;
		let submitCopy = accessNested(props, 'appData.copy.baseCopy.$VIEW_SHOWTIMES') || 'View Showtimes';
		let filtersCopy = accessNested(props, 'appData.copy.baseCopy.$FILTERS') || 'Filters';
		const closeCopy = accessNested(props, 'appData.copy.baseCopy.$FILTERS_CLOSE') || 'Close';
		const accessibilityCopy = accessNested(props, 'appData.copy.accessibilityCopy', {});
		const fontStyles = accessNested(props, 'appData.styling.fontVariables');
		const isRTL = fontStyles.textDirection === 'rtl';
		if (!filters.loading && !this.getDisplayedFilters(filters.available).length) {
			submitCopy = closeCopy;
		}
		const moreThanOneDate = this.state.datesInScroller && this.state.datesInScroller.length > 1;

		return (
			<div
				class={s.container}
				ref={(e => this.$filterContainer = e)}
			>
				<div class={s.content}>
					<div class={s.collapseHeader}>
						<ScrollBox
							class={s.dates}
							device="mobile"
							tabIndex="0"
							onKeyDown={this.datesKeyDown}
							ref={e => this.$dates = e}
							hideScrollerBar
							scrollableLeftClass={!isRTL ? s.hasMoreLeft : s.hasMoreRight}
							scrollableRightClass={!isRTL ? s.hasMoreRight : s.hasMoreLeft}
							isRTL={isRTL}
						>
							{this.renderDates()}
						</ScrollBox>
						<div class={s.headerContainer}>
							<h4 class={joinClasses(s.title, fontStyles.filterMenuTitle)}>{filtersCopy}</h4>
							<div class={joinClasses(s.scrollButtonContainer, !moreThanOneDate && s.hide)}>
								<div
									class={joinClasses(s.more, s.left)}
									onClick={this.scrollLeft}
									role="button"
									ariaLabel={accessibilityCopy.$LEFT_ARROW_DATES_ARIA}
								>
									<Chevron ariaLabel={accessibilityCopy.$LEFT_ARROW_DATES_ARIA} />
								</div>
								<div class={s.separator} />
								<div
									class={joinClasses(s.more, s.right)}
									onClick={this.scrollRight}
									role="button"
									ariaLabel={accessibilityCopy.$RIGHT_ARROW_DATES_ARIA}
								>
									<Chevron ariaLabel={accessibilityCopy.$RIGHT_ARROW_DATES_ARIA} />
								</div>
							</div>
						</div>
					</div>
					<div class={s.otherFilters}>
						{this.renderFilters()}
					</div>
					<div class={s.buttonsWrapper}>
						<div class={joinClasses(s.footer, isRTL && s.rtlFooter)}>
							<Button type="primary" class={joinClasses(s.submit, fontStyles.filterMenuViewShowtimes)} trigger={props.onSubmit} ariaLabel={accessibilityCopy.$VIEW_SHOWTIMES_ARIA}>
								{submitCopy}
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
